<template>
  <div class="sales-section h-full">
    <div class="section-header">
      <h3 class="section-title"><i class="pi pi-chart-bar"></i> Ranking de Sucursal</h3>
    </div>
    <div class="chart-wrapper flex justify-content-center relative" style="min-height: 250px;">
      
      <!-- Spinner de carga -->
      <div v-if="loading" class="flex align-items-center justify-content-center w-full absolute top-0 left-0 h-full bg-white-alpha-80 z-1">
        <ProgressSpinner style="width:50px;height:50px" />
      </div>

      <!-- Gráfico Horizontal de Barras -->
      <Chart 
        v-if="tieneDatos" 
        type="bar" 
        :data="chartData" 
        :options="chartOptions" 
        :style="{ width: '100%', height: chartHeight + 'px' }" 
      />

      <!-- Mensaje cuando no hay datos -->
      <div v-else-if="!loading" class="no-data w-full text-center p-5">
        <i class="pi pi-info-circle text-4xl text-500 mb-3"></i>
        <p class="m-0 text-600">No hay datos de sucursales para este rango</p>
      </div>

    </div>
  </div>
</template>

<script>
import ReporteService from '@/service/ReporteService';
import Chart from 'primevue/chart';
import ProgressSpinner from 'primevue/progressspinner';

export default {
  name: 'DashboardRanking',
  components: {
    Chart,
    ProgressSpinner
  },
  props: {
    fechaInicio: {
      type: Date,
      required: true
    },
    fechaFin: {
      type: Date,
      required: true
    },
    grupoIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      chartHeight: 250,
      chartData: {},
      chartOptions: {
        indexAxis: 'y', // Convertir barras a horizontales
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return ' Bs. ' + context.raw;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Ingresos (Bolivianos)'
            }
          }
        }
      }
    };
  },
  computed: {
    tieneDatos() {
      return this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0;
    }
  },
  watch: {
    fechaInicio() {
      this.cargarDatos();
    },
    fechaFin() {
      this.cargarDatos();
    },
    grupoIds: {
      handler() {
        this.cargarDatos();
      },
      deep: true
    }
  },
  created() {
    this.reporteService = new ReporteService();
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    async cargarDatos() {
      if (!this.fechaInicio || !this.fechaFin) return;
      
      this.loading = true;
      try {
        const fechaIniStr = this.fechaInicio.toISOString().split('T')[0] + ' 00:00:00';
        const fechaFinStr = this.fechaFin.toISOString().split('T')[0] + ' 23:59:59';
        
        const params = {
          fecha_inicio: fechaIniStr,
          fecha_fin: fechaFinStr
        };

        if (this.grupoIds && this.grupoIds.length > 0) {
          params.grupo_ids = this.grupoIds;
        }

        const data = await this.reporteService.getRankingSucursales(params);
        
        if (data && data.labels && data.labels.length > 0) {
          // Ordenamos de mayor a menor venta para que se vea como un ranking real
          const combinado = data.labels.map((label, i) => ({
            label,
            venta: data.ventas[i]
          }));
          combinado.sort((a, b) => b.venta - a.venta);

          // Chart.js dibuja de arriba hacia abajo en el orden del array,
          // así que invertimos para que el #1 quede arriba
          combinado.reverse();

          // Resaltamos la sucursal top (última del array invertido = primera en pantalla)
          const colores = combinado.map((item, i) =>
            i === combinado.length - 1 ? 'rgba(220, 38, 38, 0.85)' : 'rgba(99, 102, 241, 0.8)'
          );
          const bordes = combinado.map((item, i) =>
            i === combinado.length - 1 ? 'rgb(220, 38, 38)' : 'rgb(99, 102, 241)'
          );

          this.chartData = {
            labels: combinado.map(item => item.label),
            datasets: [
              {
                label: 'Total Ventas',
                data: combinado.map(item => item.venta),
                backgroundColor: colores,
                borderColor: bordes,
                borderWidth: 1,
                borderRadius: 4
              }
            ]
          };

          // Altura dinámica: ~45px por sucursal, con un mínimo razonable
          this.chartHeight = Math.max(250, combinado.length * 45);
        } else {
          this.chartData = {};
        }
      } catch (error) {
        console.error("Error cargando el ranking de sucursales:", error);
        this.chartData = {};
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.sales-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.section-title {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: 0.5rem;
}
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>